
import React, { useState } from 'react';
import { Copy, Check, Download, Share, Quote, Lightbulb, MessageCircle } from 'lucide-react';
import { GeneratedContent } from '../types';

interface ResultsDisplayProps {
  content: GeneratedContent;
  onReset: () => void;
}

const ResultsDisplay: React.FC<ResultsDisplayProps> = ({ content, onReset }) => {
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const downloadAll = () => {
    const fullText = `
KEY POINTS:
${content.keyPoints.map((p, i) => `${i + 1}. ${p}`).join('\n')}

QUOTES:
${content.quotes.map((q, i) => `"${q}"`).join('\n')}

SOCIAL MEDIA CAPTIONS:
${content.captions.map((c, i) => `Caption ${i + 1}:\n${c}\n`).join('\n')}
    `.trim();

    const element = document.createElement("a");
    const file = new Blob([fullText], {type: 'text/plain'});
    element.href = URL.createObjectURL(file);
    element.download = "wordcast-content.txt";
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  return (
    <div className="max-w-5xl mx-auto py-12 px-4 space-y-12">
      <div className="flex flex-col sm:flex-row justify-between items-center gap-6">
        <div>
          <h2 className="text-3xl font-extrabold text-slate-900">Your Generated Content</h2>
          <p className="text-slate-500 mt-1">Ready to be shared with your community</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={downloadAll}
            className="px-4 py-2 border border-slate-200 rounded-lg text-slate-700 font-medium hover:bg-slate-50 flex items-center gap-2 transition-colors"
          >
            <Download size={18} /> Download All (.txt)
          </button>
          <button
            onClick={onReset}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 flex items-center gap-2 transition-colors shadow-sm"
          >
            Create More
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Key Points */}
        <section className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="bg-yellow-100 p-2 rounded-lg text-yellow-700">
              <Lightbulb size={24} />
            </div>
            <h3 className="text-xl font-bold text-slate-900">5 Key Points</h3>
          </div>
          <ul className="space-y-4">
            {content.keyPoints.map((point, i) => (
              <li key={i} className="flex gap-4 group">
                <span className="flex-shrink-0 w-8 h-8 rounded-full bg-slate-50 text-indigo-600 font-bold flex items-center justify-center text-sm border border-slate-100">
                  {i + 1}
                </span>
                <p className="text-slate-600 leading-relaxed py-1">{point}</p>
              </li>
            ))}
          </ul>
          <button
            onClick={() => copyToClipboard(content.keyPoints.join('\n'), 'points')}
            className="mt-6 w-full py-2 border border-slate-100 rounded-lg text-xs font-semibold text-slate-500 hover:text-indigo-600 hover:bg-indigo-50/50 flex items-center justify-center gap-2 transition-all"
          >
            {copiedId === 'points' ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy All Points</>}
          </button>
        </section>

        {/* Quotes */}
        <section className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="bg-purple-100 p-2 rounded-lg text-purple-700">
              <Quote size={24} />
            </div>
            <h3 className="text-xl font-bold text-slate-900">5 Short Quotes</h3>
          </div>
          <div className="space-y-6">
            {content.quotes.map((quote, i) => (
              <div key={i} className="relative group">
                <p className="text-lg font-medium italic text-slate-700 pl-8 relative">
                  <span className="absolute left-0 top-0 text-slate-200 text-4xl font-serif">"</span>
                  {quote}
                </p>
                <button
                  onClick={() => copyToClipboard(quote, `quote-${i}`)}
                  className="mt-2 text-[10px] text-slate-400 hover:text-indigo-600 flex items-center gap-1 transition-colors"
                >
                  {copiedId === `quote-${i}` ? 'Copied!' : 'Copy Quote'}
                </button>
              </div>
            ))}
          </div>
        </section>

        {/* Social Captions */}
        <section className="lg:col-span-2 bg-slate-900 rounded-3xl p-8 sm:p-12 text-white overflow-hidden relative shadow-xl">
          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-10">
              <div className="bg-indigo-500/20 p-2 rounded-lg text-indigo-300">
                <MessageCircle size={24} />
              </div>
              <h3 className="text-2xl font-bold">3 Social Captions</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {content.captions.map((caption, i) => (
                <div key={i} className="bg-white/10 backdrop-blur-md border border-white/10 rounded-2xl p-6 flex flex-col h-full hover:bg-white/15 transition-all">
                  <div className="text-xs font-bold uppercase tracking-widest text-indigo-400 mb-4">Option {i + 1}</div>
                  <p className="text-slate-100 text-sm leading-relaxed flex-grow mb-6">{caption}</p>
                  <button
                    onClick={() => copyToClipboard(caption, `caption-${i}`)}
                    className="w-full py-3 bg-indigo-500 hover:bg-indigo-400 rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-colors"
                  >
                    {copiedId === `caption-${i}` ? <><Check size={16} /> Copied</> : <><Copy size={16} /> Copy Caption</>}
                  </button>
                </div>
              ))}
            </div>
          </div>
          
          {/* Abstract decoration for dark section */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-600/20 rounded-full blur-[100px]"></div>
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-purple-600/20 rounded-full blur-[100px]"></div>
        </section>
      </div>
    </div>
  );
};

export default ResultsDisplay;
